"use client";

// ============================================================
// CLUBHUB – Aktionen im Kopf eines Termins
// ============================================================
// Rechts im Seitenkopf: Bearbeiten als Knopf, alles Weitere im Menue
// „…“ — in den eigenen Kalender, Link kopieren, duplizieren, dieses
// Vorkommen verschieben oder absagen, den ganzen Termin absagen,
// loeschen.
//
// ABSAGEN STATT LOESCHEN: ein abgesagter Termin bleibt im Kalender
// stehen, durchgestrichen, mit Grund — so sieht jeder, dass er nicht
// stattfindet, auch wer die Mail nicht gelesen hat. Das Kalender-Abo
// meldet ihn als STATUS:CANCELLED. Geloescht wird nur, was nie haette
// angelegt werden sollen.
//
// Gespeichert wird ueber `app/(app)/kalender/actions.ts` (ohne Sitzung
// im Musterbestand: nur Erfolg melden). Benachrichtigungen an die
// Eingeladenen kommen mit dem Nachrichtenmodul.
// ============================================================
import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Ban, CalendarClock, CalendarArrowDown, CopyPlus, Link2, Pencil, RotateCcw, Trash2 } from "lucide-react";
import { buttonVariants } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogChoice,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogNotice,
  DialogRow,
  DialogSubmit,
  DialogTitle,
} from "@/components/ui/dialog";
import { DropdownItem, DropdownMenu, DropdownSeparator } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { MehrKnopf } from "@/components/ui/zeichen-knopf";
import { confirmDialog } from "@/components/ui/confirm";
import { toast } from "@/components/ui/toast";
import { errorOf, type ActionResult } from "@/lib/action-result";
import { absageZuruecknehmen, terminAbsagen, terminLoeschen, vorkommenVerschieben } from "@/app/(app)/kalender/actions";
import { icsFuerVorkommen } from "@/lib/kalender/ical";
import { formatTagMitWochentag, tagVon, uhrzeit } from "@/lib/kalender/tag";
import type { TerminView, VorkommenView } from "@/lib/kalender/views";

export function TerminAktionen({
  termin,
  vorkommen,
  eingeladen,
  vereinsname,
  jetzt,
  schreiben = true,
}: {
  termin: TerminView;
  vorkommen: VorkommenView;
  eingeladen: number;
  vereinsname: string;
  jetzt: string;
  /** Recht „Termine bearbeiten“ — ohne bleiben nur Kalenderdatei und Link. */
  schreiben?: boolean;
}) {
  const router = useRouter();
  const [dialog, setDialog] = useState<null | "absagen-vorkommen" | "absagen-termin" | "verschieben">(null);
  const serie = Boolean(termin.rrule);
  const tag = formatTagMitWochentag(tagVon(vorkommen.occurrence ?? vorkommen.startsAt));
  const abgesagt = vorkommen.status === "abgesagt";

  function herunterladen() {
    const link = `${window.location.origin}/kalender/${vorkommen.key}`;
    const text = icsFuerVorkommen(termin, vorkommen, vereinsname, link, jetzt);
    const url = URL.createObjectURL(new Blob([text], { type: "text/calendar;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `${vorkommen.title.replace(/[^\p{L}\p{N}]+/gu, "-").replace(/^-|-$/g, "").toLowerCase()}-${tagVon(vorkommen.startsAt)}.ics`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Termin als Kalenderdatei geladen", { description: "Mit einem Tipp in den eigenen Kalender übernehmen." });
  }

  async function kopieren() {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/kalender/${vorkommen.key}`);
      toast.success("Link zum Termin kopiert");
    } catch {
      toast.error("Kopieren ging nicht.");
    }
  }

  async function loeschen() {
    const ja = await confirmDialog({
      title: serie ? "Ganze Serie löschen?" : "Termin löschen?",
      description: `Zusagen, Anwesenheit und Helferschichten werden mit gelöscht. Soll nur bekannt sein, dass er nicht stattfindet, ist „Absagen“ der bessere Weg — dann bleibt er durchgestrichen im Kalender stehen.`,
      confirmLabel: "Endgültig löschen",
      danger: true,
    });
    if (!ja) return;
    const r = await terminLoeschen(termin.id);
    const fehler = errorOf(r);
    if (fehler) return void toast.error(fehler);
    toast.success(`„${termin.title}“ gelöscht`);
    router.push("/kalender");
    router.refresh();
  }

  async function zuruecknehmen() {
    const ja = await confirmDialog({
      title: "Absage zurücknehmen?",
      description: `Der Termin findet wieder statt und steht wieder regulär im Kalender und im Abo der ${eingeladen} Eingeladenen.`,
      confirmLabel: "Findet statt",
    });
    if (!ja) return;
    const r = await absageZuruecknehmen(termin.id, termin.cancelledAt ? null : vorkommen.occurrence);
    const fehler = errorOf(r);
    if (fehler) return void toast.error(fehler);
    toast.success("Absage zurückgenommen");
    router.refresh();
  }

  return (
    <>
      {schreiben && (
        <Link href={`/kalender/${termin.id}/bearbeiten${vorkommen.occurrence ? `?ab=${tagVon(vorkommen.occurrence)}` : ""}`} className={buttonVariants({ variant: "outline" })}>
          <Pencil className="h-4 w-4" aria-hidden />
          Bearbeiten
        </Link>
      )}
      <DropdownMenu trigger={<MehrKnopf />} className="min-w-[260px]">
        <DropdownItem onClick={herunterladen}>
          <CalendarArrowDown className="text-text-tertiary h-4 w-4" aria-hidden />
          In eigenen Kalender (.ics)
        </DropdownItem>
        <DropdownItem onClick={kopieren}>
          <Link2 className="text-text-tertiary h-4 w-4" aria-hidden />
          Link kopieren
        </DropdownItem>
        {schreiben && (
          <DropdownItem onClick={() => router.push(`/kalender/neu?vorlage=${termin.id}`)}>
            <CopyPlus className="text-text-tertiary h-4 w-4" aria-hidden />
            Duplizieren
          </DropdownItem>
        )}
        {schreiben && <DropdownSeparator />}
        {!schreiben ? null : abgesagt ? (
          <DropdownItem onClick={zuruecknehmen}>
            <RotateCcw className="text-text-tertiary h-4 w-4" aria-hidden />
            Absage zurücknehmen
          </DropdownItem>
        ) : (
          <>
            {serie && (
              <>
                <DropdownItem onClick={() => setDialog("verschieben")}>
                  <CalendarClock className="text-text-tertiary h-4 w-4" aria-hidden />
                  {tag} verschieben …
                </DropdownItem>
                <DropdownItem onClick={() => setDialog("absagen-vorkommen")}>
                  <Ban className="text-text-tertiary h-4 w-4" aria-hidden />
                  {tag} absagen …
                </DropdownItem>
              </>
            )}
            <DropdownItem onClick={() => setDialog("absagen-termin")}>
              <Ban className="text-text-tertiary h-4 w-4" aria-hidden />
              {serie ? "Serie absagen …" : "Termin absagen …"}
            </DropdownItem>
          </>
        )}
        {schreiben && <DropdownSeparator />}
        {schreiben && (
          <DropdownItem variant="danger" onClick={loeschen}>
            <Trash2 className="h-4 w-4" aria-hidden />
            {serie ? "Serie löschen" : "Löschen"}
          </DropdownItem>
        )}
      </DropdownMenu>

      {(dialog === "absagen-vorkommen" || dialog === "absagen-termin") && (
        <AbsageDialog
          eventId={termin.id}
          occurrence={vorkommen.occurrence}
          serie={serie}
          vorab={dialog === "absagen-vorkommen" ? "vorkommen" : serie ? "ab-hier" : "termin"}
          titel={termin.title}
          tag={tag}
          eingeladen={eingeladen}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === "verschieben" && <VerschiebenDialog eventId={termin.id} vorkommen={vorkommen} tag={tag} onClose={() => setDialog(null)} />}
    </>
  );
}

type Umfang = "vorkommen" | "ab-hier" | "termin";

function AbsageDialog({
  eventId,
  occurrence,
  serie,
  vorab,
  titel,
  tag,
  eingeladen,
  onClose,
}: {
  eventId: string;
  occurrence: string | null;
  serie: boolean;
  vorab: Umfang;
  titel: string;
  tag: string;
  eingeladen: number;
  onClose: () => void;
}) {
  const [umfang, setUmfang] = useState<Umfang>(vorab);
  const [melden, setMelden] = useState(true);

  async function absagen(form: FormData): Promise<ActionResult> {
    const grund = String(form.get("grund") ?? "").trim();
    if (grund.length > 500) return { error: "Der Grund ist zu lang (höchstens 500 Zeichen).", field: "grund" };
    // vorkommen → abweichende Zeile (seriesId/recurrenceId) mit cancelledAt und Grund;
    // ab-hier   → die naechsten Vorkommen abgesagt, danach endet die Serie;
    // termin    → Event.cancelledAt + cancelReason. Das Abo meldet STATUS:CANCELLED.
    return terminAbsagen(eventId, umfang, occurrence, grund || null);
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader>
        <DialogTitle>{umfang === "vorkommen" ? `${tag} absagen?` : serie ? "Serie absagen?" : "Termin absagen?"}</DialogTitle>
        <DialogDescription>„{titel}“ bleibt durchgestrichen im Kalender stehen, mit dem Grund darunter.</DialogDescription>
      </DialogHeader>
      <DialogForm
        action={absagen}
        success={{
          title: umfang === "vorkommen" ? `${tag} abgesagt` : "Termin abgesagt",
          description: melden ? "Benachrichtigungen an die Eingeladenen kommen mit dem Nachrichtenmodul — im Kalender-Abo steht die Absage gleich." : undefined,
        }}
      >
        <DialogContent>
          {serie && (
            <DialogChoice<Umfang>
              label="Was wird abgesagt?"
              value={umfang}
              onChange={setUmfang}
              options={[
                { value: "vorkommen", label: `Nur ${tag}`, hint: "Die übrigen Termine der Serie bleiben." },
                { value: "ab-hier", label: `Ab ${tag} alle`, hint: "Saisonende, Hallensperrung — die nächsten Termine stehen abgesagt da, dann endet die Serie." },
                { value: "termin", label: "Die ganze Serie", hint: "Auch frühere Termine erscheinen als abgesagt." },
              ]}
            />
          )}
          <Textarea name="grund" label="Grund" placeholder="z. B. Platz gesperrt, Gegner hat abgesagt …" rows={3} hint="Sehen alle Eingeladenen, auch im Kalender-Abo." maxLength={500} />
          <Switch checked={melden} onChange={setMelden} label="Eingeladene benachrichtigen" hint={`${eingeladen} Personen per E-Mail und Push, mit dem Grund.`} />
        </DialogContent>
        <DialogFooter>
          <DialogCancel />
          <DialogSubmit variant="danger">Absagen</DialogSubmit>
        </DialogFooter>
      </DialogForm>
    </Dialog>
  );
}

function VerschiebenDialog({ eventId, vorkommen, tag, onClose }: { eventId: string; vorkommen: VorkommenView; tag: string; onClose: () => void }) {
  const [fehler, setFehler] = useState<Record<string, string>>({});
  const [melden, setMelden] = useState(true);

  async function verschieben(form: FormData): Promise<ActionResult> {
    const datum = String(form.get("datum") ?? "");
    const von = String(form.get("von") ?? "");
    const bis = String(form.get("bis") ?? "");
    const f: Record<string, string> = {};
    if (!datum) f.datum = "Bitte ein Datum wählen.";
    if (!von) f.von = "Bitte eine Uhrzeit angeben.";
    if (von && bis && bis <= von) f.bis = "Das Ende liegt vor dem Beginn.";
    setFehler(f);
    const erster = Object.entries(f)[0];
    if (erster) return { error: erster[1], field: erster[0] };
    // Abweichende Zeile (recurrenceId = urspruenglicher Beginn, neuer Beginn
    // und Ende, Grund). Zusagen bleiben am Vorkommen.
    const grund = String(form.get("grund") ?? "").trim();
    return vorkommenVerschieben(eventId, vorkommen.occurrence ?? "", { tag: datum, von, bis, grund: grund || null });
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader>
        <DialogTitle>{tag} verschieben</DialogTitle>
        <DialogDescription>Nur dieses Vorkommen — die Serie bleibt, wie sie ist. Zusagen bleiben erhalten.</DialogDescription>
      </DialogHeader>
      <DialogForm
        action={verschieben}
        onError={(m, r) => {
          const feld = r && typeof r === "object" && "field" in r ? (r as { field?: string }).field : undefined;
          if (feld) setFehler({ [feld]: m });
          else toast.error(m);
        }}
        success={{ title: "Vorkommen verschoben", description: melden ? "Benachrichtigungen kommen mit dem Nachrichtenmodul — im Kalender-Abo steht die neue Zeit gleich." : undefined }}
      >
        <DialogContent>
          <DialogRow columns={3}>
            <Input name="datum" type="date" label="Neues Datum" defaultValue={tagVon(vorkommen.startsAt)} error={fehler.datum} />
            <Input name="von" type="time" label="Beginn" defaultValue={uhrzeit(vorkommen.startsAt)} error={fehler.von} />
            <Input name="bis" type="time" label="Ende" defaultValue={vorkommen.endsAt ? uhrzeit(vorkommen.endsAt) : ""} error={fehler.bis} />
          </DialogRow>
          <Textarea name="grund" label="Grund" placeholder="z. B. Platz am Donnerstag belegt" rows={2} />
          <Switch checked={melden} onChange={setMelden} label="Eingeladene benachrichtigen" hint="Mit der Bitte, die Zusage zu prüfen." />
          {vorkommen.status === "verschoben" && vorkommen.movedFrom && (
            <DialogNotice tone="warning" icon={<CalendarClock className="text-warning h-4 w-4" aria-hidden />}>
              Schon einmal verschoben — ursprünglich {formatTagMitWochentag(tagVon(vorkommen.movedFrom))}, {uhrzeit(vorkommen.movedFrom)} Uhr.
            </DialogNotice>
          )}
        </DialogContent>
        <DialogFooter>
          <DialogCancel />
          <DialogSubmit>Verschieben</DialogSubmit>
        </DialogFooter>
      </DialogForm>
    </Dialog>
  );
}
